// src/lib/dogIdentity.js
//
// Identidad estable de un perro/cliente, independiente de su nombre visible.
//
// Reglas:
// - `id` se genera UNA SOLA VEZ, en el momento de creación de un perro
//   (alta manual en handleAddDogToList, o alta por importación de PDF de
//   un cliente nuevo en dogDataParsers.js). Nunca se regenera al leer,
//   sincronizar o reordenar.
// - `importKey` es el nombre normalizado en el momento de la primera
//   importación PDF de ese cliente. Sirve para volver a emparejar el mismo
//   cliente en importaciones futuras del PDF aunque su `name` visible haya
//   sido renombrado manualmente después. También se asigna una sola vez.
// - `name` es el dato visible y editable en todo momento.
// - Retrocompatibilidad: los perros creados ANTES de este cambio no tienen
//   `id`. Para ellos, `getDogIdentity` devuelve el nombre normalizado
//   (igual que el comportamiento actual), así que las claves de estado
//   (completedDogs/urgentDogs) de datos ya existentes NO cambian hasta que
//   ese perro reciba un `id` (backfill perezoso la primera vez que se edita).

/**
 * Genera un identificador permanente para un perro nuevo.
 */
export function generateDogId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Normaliza un nombre para comparación/almacenamiento (trim + mayúsculas).
 */
export function normalizeDogName(name) {
  return (name || "").trim().toUpperCase();
}

/**
 * Devuelve la identidad estable de un perro:
 * - Si tiene `id` real, lo devuelve tal cual (estable frente a renombrados).
 * - Si no (registro antiguo, sin backfill todavía), devuelve el nombre
 *   normalizado — exactamente el mismo valor que se usaba antes de este
 *   cambio, para no romper claves de estado existentes.
 */
export function getDogIdentity(dog) {
  if (!dog) return null;
  return dog.id || normalizeDogName(dog.name);
}

/**
 * Compara si un perro corresponde a una identidad dada.
 */
export function dogMatchesIdentity(dog, identity) {
  if (!dog || !identity) return false;
  return getDogIdentity(dog) === identity;
}

/**
 * Busca un perro dentro de una lista por identidad estable (id real,
 * o nombre normalizado si aún no tiene id).
 */
export function findDogByIdentity(list, identity) {
  if (!Array.isArray(list) || !identity) return undefined;
  return list.find((d) => dogMatchesIdentity(d, identity));
}

export function findDogIndexByIdentity(list, identity) {
  if (!Array.isArray(list) || !identity) return -1;
  return list.findIndex((d) => dogMatchesIdentity(d, identity));
}

/**
 * Construye la clave de estado (completedDogs / urgentDogs) para un perro
 * en un día concreto, usando su identidad estable (id o nombre normalizado).
 */
export function makeDogStateKey(dayKey, dog) {
  return `${dayKey}-${getDogIdentity(dog)}`;
}

/**
 * Construye la clave de estado a partir de un dayKey y una identidad ya
 * resuelta (cuando no se dispone del objeto `dog` completo).
 */
export function makeStateKeyFromIdentity(dayKey, identity) {
  return `${dayKey}-${identity}`;
}

/**
 * Fase 2 — Resolución de identidad en importación PDF.
 *
 * Dado el `importKey` (nombre normalizado) de una línea del PDF y la lista
 * `templateDogs` actual del perfil, busca si ese cliente YA fue importado
 * antes:
 *  1. Coincidencia exacta por `importKey` (caso normal: el cliente ya tiene
 *     identidad estable asignada en una importación anterior).
 *  2. Fallback legacy: entradas de plantilla ANTERIORES a este cambio, que
 *     no tienen `importKey` todavía. Para esas, se compara por nombre
 *     normalizado (comportamiento equivalente al anterior por nombre).
 *
 * Devuelve la entrada de plantilla encontrada (o `undefined` si es un
 * cliente nuevo). Nunca genera `id` aquí: la generación ocurre en el
 * llamador únicamente cuando no hay coincidencia.
 */
export function findTemplateMatchForImportKey(templateDogs, importKey) {
  if (!Array.isArray(templateDogs) || !importKey) return undefined;
  const byImportKey = templateDogs.find((d) => d?.importKey && d.importKey === importKey);
  if (byImportKey) return byImportKey;
  return templateDogs.find(
    (d) => !d?.importKey && normalizeDogName(d?.name) === importKey
  );
}


